import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import {useEffect, useState} from 'react'

import {useThrottle} from '../../../../../hooks/useThrottle'
import {restApi} from '../../../../../services/rest'
import useMatrixParamBuildFieldControl from '../../hooks/useMatrixParamBuildFieldControl'
import {getParameterValueErrorMessage} from '../../MatrixParamBuild.utils'

import {THROTTLE_TIME_AUTOCOMPLETION} from './MatrixParamBuildParameterValue.consts'

function useMatrixParamBuildParameterValue(
  selectValue: string | null,
  parameterIndex: number,
  index: number,
  isDuplicate: boolean,
) {
  const [isParameterAutocompletion, setParameterAutocompletion] = useState<boolean>(false)
  const fieldName = `[${parameterIndex}].value.[${index}]`
  const {errorMessage, buildTypeId, changeValueHandler, isFieldTouched, setErrorMessageHandler} =
    useMatrixParamBuildFieldControl(fieldName)
  const [fetchParameterAutocompletion, {autocompletionOptions = []}] =
    restApi.endpoints.getParameterAutocompletion.useLazyQuery({
      selectFromResult: ({data}) => ({
        autocompletionOptions: data?.map(item => ({
          key: item.value,
          label: item.value,
          isResetItem: item.value.includes('--'),
          custom: true,
          details: item.meta.replace(/<[^>]*>?/gm, ''),
        })),
      }),
    })
  const fetchParameterAutocompletionWithThrottle = useThrottle(
    fetchParameterAutocompletion,
    THROTTLE_TIME_AUTOCOMPLETION,
  )
  const parameterAutocompletionHandler = (value: string) => {
    const projectParams = value.split('%')
    const isRequestProjectParams = Boolean(projectParams.length > 1 && !(projectParams.length % 2))

    if (isRequestProjectParams) {
      const term = projectParams.at(-1)

      fetchParameterAutocompletionWithThrottle({
        settingsId: `buildType:${buildTypeId}`,
        term,
      })
    }

    setParameterAutocompletion(isRequestProjectParams)
  }
  const selectFilterHandler = (itemToCheck: SelectItem, checkString: string) => {
    if (isParameterAutocompletion) {
      return true
    }

    return `${itemToCheck.label}`.toLowerCase().includes(checkString.toLowerCase())
  }
  const selectChangeHandler = (selectItem: SelectItem | null) => {
    const isCustom = selectItem?.custom
    const {label: selectLabel} = selectItem ?? {}
    const labelString = isCustom ? `${selectValue}${selectLabel}%` : selectLabel?.toString() ?? ''
    const selectErrorMessage = getParameterValueErrorMessage(labelString, isDuplicate)
    const seporator = '=>'
    let [label = '', value] = labelString.split(seporator)

    setErrorMessageHandler(selectErrorMessage, fieldName, true)

    if (typeof value !== 'string') {
      ;[value, label] = [label, value]
    }

    changeValueHandler({
      ...(Boolean(label) && {label}),
      value: value ?? '',
    })

    parameterAutocompletionHandler(labelString)
  }

  useEffect(() => {
    const parameterErrorMessage = getParameterValueErrorMessage(selectValue, isDuplicate)

    if (isFieldTouched || isDuplicate) {
      setErrorMessageHandler(parameterErrorMessage, fieldName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFieldTouched, isDuplicate])

  return {
    errorMessage,
    isParameterAutocompletion,
    autocompletionOptions,
    selectChangeHandler,
    selectFilterHandler,
  }
}

export default useMatrixParamBuildParameterValue
